import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { badge, zeb } from '../../lib/format';
import { cardPad, Th } from '../../lib/ui';

const TITLE_TH: Record<string, string> = {
  'Fleet Insurance Agreement 2026-2027': 'สัญญาประกันภัยกองยานพาหนะ 2026-2027',
  'Property All-Risk Cover': 'ประกันภัยทรัพย์สินแบบครอบคลุมทุกความเสี่ยง',
  'Spare Parts Supply — Annual': 'จัดหาชิ้นส่วนอะไหล่ — รายปี',
  'Maintenance & Servicing MSA': 'สัญญาบริการบำรุงรักษา (MSA)',
};
const STATUS_TH: Record<string, string> = { 'Active': 'ใช้งาน', 'Expired': 'หมดอายุ' };

export default function Contract() {
  const { go, L } = useApp();

  const contracts = [
    { id: 'CON-2026-0014', title: 'Fleet Insurance Agreement 2026-2027', sup: 'Apex Insurance Co.', value: '$4.62M', end: '2027-06-30', days: 361, signed: 'Walter A.', status: 'Active', renewal: false },
    { id: 'CON-2026-0009', title: 'Property All-Risk Cover', sup: 'Summit Underwriters', value: '$5.40M', end: '2026-09-12', days: 70, signed: 'Walter A.', status: 'Active', renewal: true },
    { id: 'CON-2025-0058', title: 'Spare Parts Supply — Annual', sup: 'GeoTrack Solutions', value: '$6.30M', end: '2026-07-25', days: 21, signed: 'Maria B.', status: 'Active', renewal: true },
    { id: 'CON-2025-0041', title: 'Maintenance & Servicing MSA', sup: 'Pacific Insurance Co.', value: '$2.58M', end: '2026-06-20', days: -14, signed: 'Maria B.', status: 'Expired', renewal: false },
  ];
  const contractKpis = [
    { label: L('Active Contracts', 'สัญญาที่ใช้งาน'), value: '3', color: 'var(--color-fg-1)' },
    { label: L('Expiring ≤90 days', 'ใกล้หมดอายุ ≤90 วัน'), value: '2', color: 'var(--color-warning)' },
    { label: L('Total Contract Value', 'มูลค่าสัญญารวม'), value: '$18.90M', color: 'var(--color-fg-1)' },
    { label: L('Auto-Renewals', 'ต่ออายุอัตโนมัติ'), value: '2', color: 'var(--color-fg-1)' },
  ];
  const contractRows = contracts.map((c, i) => {
    const b = badge(c.status);
    const daysColor = c.days < 0 ? 'var(--color-error)' : c.days <= 90 ? 'var(--color-warning)' : 'var(--color-success)';
    const daysText = c.days < 0 ? L('Expired', 'หมดอายุ') : c.days + L(' days', ' วัน');
    return {
      ...c,
      zebra: zeb(i), stBg: b.bg, stFg: b.fg, daysColor, daysText,
      titleLabel: L(c.title, TITLE_TH[c.title] ?? c.title),
      statusLabel: L(c.status, STATUS_TH[c.status] ?? c.status),
    };
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 13, color: 'var(--color-fg-1)' }}>
        <Icon name="alert-triangle" style={{ color: 'var(--color-warning)' }} />{L('2 contracts require renewal action. Review expiring agreements below.', 'สัญญา 2 ฉบับต้องดำเนินการต่ออายุ ตรวจสอบสัญญาที่ใกล้หมดอายุด้านล่าง')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        {contractKpis.map((k) => (
          <div key={k.label} style={cardPad('16px 18px')}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-mono)', color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>
      <div style={cardPad(0, 'auto')}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 960 }}>
          <thead>
            <tr style={{ background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)' }}>
              <Th>{L('Contract ID', 'รหัสสัญญา')}</Th>
              <Th>{L('Title', 'ชื่อสัญญา')}</Th>
              <Th>{L('Supplier', 'ซัพพลายเออร์')}</Th>
              <Th align="right">{L('Value', 'มูลค่า')}</Th>
              <Th>{L('End Date', 'วันสิ้นสุด')}</Th>
              <Th>{L('Remaining', 'คงเหลือ')}</Th>
              <Th>{L('Signed By', 'ลงนามโดย')}</Th>
              <Th>{L('Status', 'สถานะ')}</Th>
              <Th align="right">{L('Actions', 'การดำเนินการ')}</Th>
            </tr>
          </thead>
          <tbody>
            {contractRows.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--color-border)', background: c.zebra }}>
                <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.id}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{c.titleLabel}</span>
                    {c.renewal && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>{L('Renewal Required', 'ต้องต่ออายุ')}</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--color-fg-2)' }}>{c.sup}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.value}</td>
                <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', color: 'var(--color-fg-2)' }}>{c.end}</td>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: c.daysColor }}>{c.daysText}</td>
                <td style={{ padding: '12px 16px', color: 'var(--color-fg-2)' }}>{c.signed}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: c.stBg, color: c.stFg }}>{c.statusLabel}</span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <button onClick={() => go('contractdetail')} className="hv-ink" style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-strong)', background: 'var(--color-white)' }}>{L('View', 'ดู')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
